/**
 * The promotions store (DISCOUNT_V2_PLAN.md §9.10, decision 35; R2.4e): one store for what outlives a
 * screen, shared by the discounts flow and loyalty v2. Not persisted (as v1's two stores were not), and no
 * saga: the flows call the partner port (`../partners/port.ts`) and record what it answered here.
 *
 * On logout it keeps what v1 kept: a user logout forgets the programmes (v1's `LoyaltyStore` cleared
 * `programDetailsMap`), a support logout forgets the programmes and every customer's loyalty record (v1
 * reset its whole loyalty store). The approval record is not here until cutover (decision 45); when it
 * comes, a logout must not forget it (v1's `discountStore` reset nothing on logout).
 */
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { supportLogout, userLogout } from "../../redux-store/slices/LoginSlice/actions";
import type { LoyaltyRecord, Programme, PromotionsState } from "./types";

export const EMPTY_PROMOTIONS: PromotionsState = { loyaltyRecords: {}, programmes: {} };

const slice = createSlice({
  name: "promotions",
  initialState: EMPTY_PROMOTIONS,
  reducers: {
    /** What a partner answered about a customer's loyalty. */
    loyaltyRecordFetched: (state, action: PayloadAction<{ customer: string; record: LoyaltyRecord }>) => {
      const { customer, record } = action.payload;
      state.loyaltyRecords[customer] = { ...state.loyaltyRecords[customer], [record.partner]: record };
    },
    /** A partner changed the customer's record (the port's `customerRecordStale`): fetch it again. */
    loyaltyRecordForgotten: (state, action: PayloadAction<{ customer: string; partner: string }>) => {
      const { customer, partner } = action.payload;
      delete state.loyaltyRecords[customer]?.[partner];
    },
    programmeFetched: (state, action: PayloadAction<{ channelDefId: string; programme: Programme }>) => {
      state.programmes[action.payload.channelDefId] = action.payload.programme;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(userLogout, (state) => {
        state.programmes = {};
      })
      .addCase(supportLogout, (state) => {
        state.programmes = {};
        state.loyaltyRecords = {};
      });
  },
});

export const { loyaltyRecordFetched, loyaltyRecordForgotten, programmeFetched } = slice.actions;
export const promotionsReducer = slice.reducer;
